import React from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetResultBlobs,
  GetResultBlobsVariables,
} from 'insights/analytics-visualizations/blobs/__generated/GetResultBlobs';
import {BLOB_QUERY} from 'insights/analytics-visualizations/blobs/QueryingBlobLinkList';
import {BlobLinkCollapsiblePreview} from 'notebook/data-tables/BlobLinkList';
import {ChipSize} from 'ui-library/chips/Chip';

interface BlobPreviewProps {
  blobIds: Array<string>;
}

/**
 * Fetches and renders a list of collapsible blob previews to be used in the PlateRecordMetadata component,
 * given a list of blob ids.
 */
export function BlobPreview(props: BlobPreviewProps): JSX.Element | null {
  const result = useSkippableBenchlingQuery<GetResultBlobs, GetResultBlobsVariables>(BLOB_QUERY, {
    variables: {
      ids: props.blobIds,
    },
  });

  if (result.type === QueryResultType.PENDING) {
    return <div>Loading...</div>;
  }

  if (result.type === QueryResultType.ERROR) {
    return <div>Error loading blobs</div>;
  }

  if (result.type === QueryResultType.SUCCESS) {
    const blobs: Array<JSX.Element> = [];
    for (const blob of result.data.resultBlobs) {
      if (isInaccessible(blob)) {
        continue;
      }

      blobs.push(
        <BlobLinkCollapsiblePreview
          key={blob.id}
          {...blob}
          /**
           * Truncate name so that the preview & download buttons aren't pushed off the screen
           * due to the small width of the sidebar
           */
          name={blob.name.length > 20 ? `${blob.name.slice(0, 20)}...` : blob.name}
          pdfPreviewFileJson={null}
          uploadStatus={null}
          chipSize={ChipSize.SMALL}
          initiallyExpanded={result.data.resultBlobs.length === 1}
          shouldPreviewImages={true}
          // TODO[BNCH-78973]: Need to make this field not optional
          owningEntityApiId=""
        />
      );
    }
    return <div>{blobs}</div>;
  }
  return null;
}
